import { useEffect, useState } from "react";

import api from "../api/client";
import { useSort, sortRows, SortableTh } from "./SortableTh";
import { formatDate, formatDateTime } from "../utils/datetime";

/**
 * Per-customer Meraki license card. Shows each license with expiry,
 * days remaining, and a color-coded severity pill. The daily
 * scheduler does the sync automatically; this card also exposes a
 * "Sync now" button for admins who want fresh data on demand.
 */

type License = {
  id: number;
  customer_id: number;
  license_id: string;
  edition: string | null;
  device_serial: string | null;
  state: string | null;
  license_key_suffix: string | null;
  claim_date: string | null;
  activation_date: string | null;
  expiration_date: string | null;
  days_remaining: number | null;
  severity: "ok" | "warning" | "critical" | "expired" | null;
  total_duration_days: number | null;
  alert_30d_sent_at: string | null;
  alert_7d_sent_at: string | null;
  calendar_event_id: number | null;
  last_synced_at: string | null;
};

const SEV_PILL: Record<string, string> = {
  ok: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  warning: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-300",
  critical: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  expired: "bg-red-200 text-red-900 font-bold dark:bg-red-900/50 dark:text-red-200",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return formatDate(iso);
}

export default function MerakiLicensesCard({
  customerId,
  isAdmin,
}: {
  customerId: number;
  isAdmin: boolean;
}) {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const licSort = useSort("expiration_date", "asc");

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const r = await api.get(`/api/v1/customers/${customerId}/meraki-licenses`);
      setLicenses(r.data || []);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load Meraki licenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const syncNow = async () => {
    setErr("");
    setInfo("");
    setSyncing(true);
    try {
      const r = await api.post(`/api/v1/customers/${customerId}/meraki-licenses/sync`);
      setInfo(`Synced ${r.data?.synced ?? 0} license(s).`);
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const lastSync = licenses[0]?.last_synced_at;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <div className="flex items-start justify-between mb-3 gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Meraki licenses
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Pulled from the Meraki Dashboard daily at 6:00 AM ET. Alerts fire
            30 days before expiration and escalate to critical at 7 days. Each
            expiration also lands on the in-app calendar with a 30-day reminder.
          </p>
          {lastSync && (
            <p className="text-[11px] text-gray-400 mt-1">
              Last synced {formatDateTime(lastSync)}
            </p>
          )}
        </div>
        {isAdmin && (
          <button
            onClick={syncNow}
            disabled={syncing}
            className="text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        )}
      </div>

      {err && (
        <div className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 rounded-lg mb-3">
          {err}
        </div>
      )}
      {info && (
        <div className="text-xs text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 px-3 py-2 rounded-lg mb-3">
          {info}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-gray-500 italic">Loading…</p>
      ) : licenses.length === 0 ? (
        <div className="text-xs text-gray-600 dark:text-gray-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 space-y-2">
          <p>
            <strong>No Meraki integration configured for this customer.</strong>{" "}
            To track licenses, you need to add the customer's Meraki API key first.
          </p>
          <ol className="list-decimal list-inside space-y-1 text-[11px]">
            <li>
              Open{" "}
              <a href="/network-discovery" className="text-brand dark:text-brand-light underline font-semibold">
                Network Discovery
              </a>{" "}
              (sidebar) → <em>Network integrations</em> → <strong>Add integration</strong>.
            </li>
            <li>
              Pick vendor <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">meraki</code>{" "}
              + this customer + paste the Meraki Dashboard API key.
            </li>
            <li>
              Optional: under <em>Extra config</em>, set <code>organization_id</code>{" "}
              if the key has access to multiple Meraki orgs and you want to lock this
              integration to one.
            </li>
            <li>
              Click <strong>Probe</strong> on the new row to verify the key, then come
              back here and hit <strong>Sync now</strong>.
            </li>
          </ol>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
            <tr>
              <SortableTh label="Edition" sortKey="edition" sort={licSort.sort} onSort={licSort.toggle} />
              <SortableTh label="Device serial" sortKey="device_serial" sort={licSort.sort} onSort={licSort.toggle} />
              <SortableTh label="State" sortKey="state" sort={licSort.sort} onSort={licSort.toggle} />
              <SortableTh label="Expires" sortKey="expiration_date" sort={licSort.sort} onSort={licSort.toggle} />
              <SortableTh label="Days left" sortKey="days_remaining" sort={licSort.sort} onSort={licSort.toggle} />
              <th className="text-left px-3 py-2 text-xs font-medium">Calendar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortRows(licenses, licSort.sort, (lic, key) => {
              switch (key) {
                case "device_serial": return (lic.device_serial || "").toLowerCase();
                case "state": return (lic.state || "").toLowerCase();
                case "expiration_date": return lic.expiration_date ? Date.parse(lic.expiration_date) : 0;
                case "days_remaining": return lic.days_remaining ?? 0;
                default: return (lic.edition || "").toLowerCase();
              }
            }).map((lic) => {
              const pill = SEV_PILL[lic.severity || "ok"] || SEV_PILL.ok;
              return (
                <tr key={lic.id} className="text-gray-900 dark:text-gray-100">
                  <td className="px-3 py-2">
                    <div className="font-medium text-sm">
                      {lic.edition || "—"}
                    </div>
                    {lic.license_key_suffix && (
                      <div className="text-[10px] text-gray-500 font-mono">
                        …{lic.license_key_suffix}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono">
                    {lic.device_serial || <span className="italic text-gray-400">co-term / org-wide</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{lic.state || "—"}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(lic.expiration_date)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${pill}`}>
                      {lic.severity === "expired"
                        ? `expired ${Math.abs(lic.days_remaining || 0)}d ago`
                        : `${lic.days_remaining ?? "?"} days`}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {lic.calendar_event_id ? (
                      <a
                        href="/calendar"
                        className="text-brand dark:text-brand-light hover:underline"
                        title="Open the calendar (the expiry event sits there)"
                      >
                        On calendar →
                      </a>
                    ) : (
                      <span className="text-gray-400 italic">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
